import { computed, type ComputedRef, unref } from 'vue-demi'
import { type MaybeRef } from '@vueuse/core'
import { uniq } from 'lodash-es'
import { GridTargetConfig, LayoutTargetConfig, ListTargetConfig } from '../types'
import { Position, Target } from '../types/primitives'

export function useActivePositionsMap<TConfig extends LayoutTargetConfig = LayoutTargetConfig> (currentTargetMap: MaybeRef<Record<Target, TConfig>>) {
  function getActivePositions (name: string, layoutConfigRef: MaybeRef<TConfig | undefined>): ComputedRef<Position[]> {
    return computed(() => {
      const layoutConfig = unref(layoutConfigRef)
      if (layoutConfig?.disabled === true) return []
      else if (layoutConfig != null && 'grid' in layoutConfig) {
        const _item = layoutConfig as GridTargetConfig
        return _item.grid?.area?.flat(2) ?? []
      } else if (layoutConfig != null && 'positions' in layoutConfig && layoutConfig?.positions != null && Array.isArray(layoutConfig.positions)) {
        const _item = layoutConfig as ListTargetConfig
        return _item.positions as string[]
      } else {
        return [name]
      }
    })
  }

  const activePositionsMap: ComputedRef<Record<Target, Position[] | undefined>> = computed(() => {
    return Object.keys(currentTargetMap.value).reduce<Record<Target, Position[] | undefined>>((acc, key) => {
      acc[key] = getActivePositions(key, unref(currentTargetMap)[key]).value
      return acc
    }, {})
  })

  const flatActivePositions: ComputedRef<Position[]> = computed(() => {
    const flatten = Object.values(unref(activePositionsMap)).filter((i): i is string[] => i != null).flat(2)
    return uniq(flatten)
  })

  function getActivePositionsRefByTarget (name: Target): ComputedRef<Position[]> {
    return computed(() => {
      const activePositions = unref(activePositionsMap)[name]
      return activePositions ?? []
    })
  }

  return {
    activePositionsMap,
    getActivePositionsRefByTarget,
    flatActivePositions
  }
}
